"use client";

import { useEffect, useRef, useState } from "react";
import { X, MousePointerClick } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { dismissBetLinkNudge } from "@/lib/actions/bet-link-nudge";
import { phCapture } from "@/utils/analytics";

const SHOW_DELAY_MS = 3000;

interface BetLinkNudgeProps {
  dismissed: boolean;
}

export function BetLinkNudge({ dismissed }: BetLinkNudgeProps) {
  const { subscriptions } = useAuth();
  const [isDismissed, setIsDismissed] = useState(dismissed);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const shownTrackedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const isTrialUser = subscriptions.some((s) => s.status === "trialing");

  useEffect(() => {
    if (isDismissed || !isTrialUser) return;

    timerRef.current = setTimeout(() => {
      setShouldRender(true);
      requestAnimationFrame(() => {
        setIsVisible(true);
        if (!shownTrackedRef.current) {
          shownTrackedRef.current = true;
          phCapture("bet_link_nudge_shown");
        }
      });
    }, SHOW_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isDismissed, isTrialUser]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  function handleDismiss() {
    setIsVisible(false);
    setIsDismissed(true);

    phCapture("bet_link_nudge_dismissed");

    dismissBetLinkNudge().catch(() => {});

    closeTimerRef.current = setTimeout(() => {
      setShouldRender(false);
    }, 200);
  }

  if (!shouldRender) return null;

  return (
    <section
      className={cn(
        "mx-3 sm:mx-4 mt-2 overflow-hidden rounded-panel",
        "transition-[max-height,opacity] duration-200 ease-out motion-reduce:transition-none",
        isVisible
          ? "max-h-14 opacity-100"
          : "max-h-0 opacity-0 pointer-events-none"
      )}
      role="status"
      aria-live="polite"
    >
      <section className="relative border border-primary/20 bg-primary/[0.06] rounded-panel">
        <div className="absolute inset-y-0 left-0 w-[2px] rounded-l-panel bg-gradient-to-b from-primary/60 via-primary/30 to-transparent" />

        <div className="h-10 pl-4 pr-3 flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <MousePointerClick className="size-3.5 shrink-0 text-primary" />
            <p className="text-[13px] min-w-0 truncate">
              <span className="font-medium text-foreground">
                Tip:
              </span>
              <span className="text-muted-foreground">
                {" "}Click any sportsbook odds to place your bet directly
              </span>
              <span className="hidden sm:inline text-muted-foreground">
                {" "}&mdash; look for the sportsbook icons in each row.
              </span>
            </p>
          </div>

          <div className="ml-auto shrink-0">
            <button type="button"
              onClick={handleDismiss}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
              aria-label="Dismiss tip"
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>
      </section>
    </section>
  );
}
